import React from 'react';
import PropTypes from 'prop-types';
import { H5 } from 'components/Typography';
import { FieldArray } from 'react-final-form-arrays';
import styled from 'styled-components/macro';
import { theme } from 'styled-tools';
import { Col, CustomInput } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { ErrorMessage } from 'components/Form';
import { Link } from 'react-router-dom';
import { SortableContainer, SortableElement } from 'react-sortable-hoc';
import DragHandle from '../DragHandle';
import ModuleCard from '../ModuleCard';
import { MODULE_PUBLISHING_STATUSES } from 'utils/consts';
import SmallTooltip, { StyledUncontrolledTooltip as DesktopTooltip } from 'components/Tooltips/SmallTooltip';

const CardWrapper = styled.div`
  padding: 1rem;
`;

const TitleWrapper = styled.div`
  margin: 0.5rem 0;
  display: block;
`;

const SideBarModuleLabel = styled(Link)`
  margin-right: 0.2rem;
  color: ${theme('colors.black')};
  font-weight: ${theme('fontWeightBold')};
`;

const RemoveLinkColumn = styled(Col)`
  &&& {
    display: block;
    text-align: center;
  }
`;

const RemoveLink = styled.a`
  padding: 0.5rem;
  margin: auto;
  display: block;

  && {
    color: ${theme('colors.darkRed')};
  }
`;

const StyledInput = styled(CustomInput)`
  vertical-align: middle;

  label {
    padding-top: 0;
    padding-bottom: 0;
  }
`;

const ArchivedLabel = styled.span`
  display: block;
  margin-bottom: 0.5rem;
  color: ${theme('colors.darkerGray')};
  font-size: 0.8rem;
  font-weight: bold;
`;

const RemoveIcon = styled(FontAwesomeIcon).attrs({
  icon: faTimes,
})`
  margin-right: 0.5em;
`;

const MobileToolTip = styled.div`
  display: initial;
  @media (min-width: 1200px) {
    display: none;
  }
`;

const SortableModuleCard = SortableElement(props => <ModuleCard {...props} />);
const SortableModuleList = SortableContainer(({ modules, handleRemove, handleSwitch, disabled }) => {
  return (
    <CardWrapper>
      {modules.map((module, index) => (
        <SortableModuleCard key={`added_module_${module.module.id}`} index={index}>
          {!disabled && <DragHandle />}
          <div className='card-content'>
            <Col>
              <TitleWrapper>
                <SideBarModuleLabel
                  to={`/module/${module.module.id}`}
                  target='_blank'
                  id={`desktop_module_card_${index}`}
                >
                  {module.module.title}
                </SideBarModuleLabel>
                <MobileToolTip>
                  <SmallTooltip
                    placement='right'
                    id={`module_card_${index}`}
                    text={(module?.module?.description ?? '').replace(/<[^>]*>?/gm, '')}
                    label='Description'
                  />
                </MobileToolTip>
              </TitleWrapper>

              <DesktopTooltip placement='right' target={`desktop_module_card_${index}`}>
                {(module?.module?.description ?? '').replace(/<[^>]*>?/gm, '')}
              </DesktopTooltip>

              {module?.module?.publishingStatus === MODULE_PUBLISHING_STATUSES.ARCHIVED && (
                <ArchivedLabel>
                  This module has been retired by Infinitec. Campaign Members currently using it can still access it
                  from their user dashboard. If you remove it here, you will not be able to access it again.
                </ArchivedLabel>
              )}
              {module?.module?.quiz?.enabled && (
                <StyledInput
                  type='switch'
                  id={`includeQuiz-${module.module.id}`}
                  name='includeQuiz'
                  label='Include Quiz'
                  checked={module.includeQuiz}
                  onClick={() => handleSwitch(index)}
                  disabled={disabled}
                />
              )}
            </Col>
            {!disabled && (
              <RemoveLinkColumn>
                <RemoveLink onClick={() => handleRemove(index)}>
                  <RemoveIcon title='Remove' />
                  Remove Module
                </RemoveLink>
              </RemoveLinkColumn>
            )}
          </div>
        </SortableModuleCard>
      ))}
    </CardWrapper>
  );
});

function ModuleCards({ fields, meta, disabled }) {
  const handleRemove = i => {
    fields.remove(i);
  };

  const handleSwitch = i => {
    const module = fields.value[i];
    fields.update(i, {
      ...module,
      includeQuiz: !module.includeQuiz,
    });
  };

  const onSortEnd = ({ oldIndex, newIndex }) => {
    fields.move(oldIndex, newIndex);
  };

  if (!fields.value || fields.value.length === 0) {
    return (
      <>
        <p>No Modules.</p>
        {meta.error && meta.touched && <ErrorMessage>{meta.error}</ErrorMessage>}
      </>
    );
  }

  return (
    <SortableModuleList
      modules={fields.value}
      handleRemove={handleRemove}
      handleSwitch={handleSwitch}
      onSortEnd={onSortEnd}
      useDragHandle={true}
      disabled={disabled}
    />
  );
}

function LearningPathSidebar({ disabled = false }) {
  return (
    <div>
      <H5>Modules Included</H5>
      <FieldArray name='modules' component={ModuleCards} disabled={disabled} />
    </div>
  );
}

LearningPathSidebar.propTypes = {
  disabled: PropTypes.bool,
};

export default LearningPathSidebar;
